import { planbookColors, planbookFonts, fonts, fontSizes, emailFormat } from '../constants';
import { genSaltSync, hashSync, setRandomFallback } from 'bcryptjs';
import ld from 'lodash';
import isaac from 'isaac';
import store from '@/store';

setRandomFallback((len: number): number[] => {
  const buf = new Uint8Array(len);
  return Array.from<number>(buf.map(() => Math.floor(isaac.random() * 256)));
});

type IteratorCallback = {
  (item: any, i: number, arr: Array<any>): void;
};

type ValueConverter = {
  (value: any): any;
}

export default class CommonUtils {
  public static getColor(color: string): string {
    if (isNaN(+color)) {
      return color;
    } else {
      return planbookColors[+color];
    }
  }

  public static hasValue(value: any) {
    return value !== undefined && value !== null;
  }

  public static hasText(text: string): boolean {
    return CommonUtils.hasValue(text) && text.trim() !== '';
  }

  public static hasNoText(text: string): boolean {
    return !CommonUtils.hasText(text);
  }

  public static getFontFamily(font: string): string {
    if (isNaN(+font)) {
      return font;
    } else {
      return planbookFonts[+font];
    }
  }

  public static getFontSizeLabel(size: string) {
    if (isNaN(+size)) {
      return size;
    } else {
      for (const i in fontSizes) {
        const f = fontSizes[i];
        if (f.value === '' + size) {
          return f.text;
        }
      }
      return '10pt';
    }
  }

  public static getFontSizeValue(size: string) {
    for (const i in fontSizes) {
      const f = fontSizes[i];
      if (f.text === '' + size) {
        return f.value;
      }
    }
    return '10';
  }

  public static getFontLabel(font: string) {
    if (isNaN(+font)) {
      return font;
    } else {
      for (const i in fonts) {
        const f = fonts[i];
        if (f.value === '' + font) {
          return f.text;
        }
      }
      return 'Arial';
    }
  }

  public static getFontValue(font: string) {
    for (const i in fonts) {
      const f = fonts[i];
      if (f.text === '' + font) {
        return f.value;
      }
    }
    return '1';
  }

  public static isIn(needle: any, haystack: Array<any>) {
    if (!needle || !haystack) {
      return false;
    }
    return haystack.includes(needle);
  }

  public static isArrayEqual(a: Array<any>, b: Array<any>) {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    a.sort();
    b.sort();

    for (let i = 0; i < a.length; ++i) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  }

  public static isArrayNotEqual(a: Array<any>, b: Array<any>) {
    return !CommonUtils.isArrayEqual(a, b);
  }

  public static isEmpty = (arr: Array<any>) => {
    return !arr || arr.length === 0;
  }

  public static isNotEmpty(arr: Array<any>) {
    return !CommonUtils.isEmpty(arr);
  }

  public static forEach(arr: Array<any>, fn: IteratorCallback) {
    if (!arr || !arr.length || !fn) return
    let i = -1
    const len = arr.length
    while (++i < len) {
      const item = arr[i]
      fn(item, i, arr)
    }
  }

  public static forEachValues(o: any, fn: IteratorCallback) {
    if (!o || Array.isArray(o)) {
      return;
    }
    const arr = [];
    for (const key in o) {
      arr.push(o[key]);
    }
    CommonUtils.forEach(arr, fn);
  }

  public static getOrDefaultTo(v: any, d?: any) {
    return v || d;
  }

  public static compareString(a: string, b: string) {
    if (!a && b) {
      return -1;
    } else if (a && !b) {
      return 1;
    } else {
      return a.localeCompare(b);
    }
  }

  public static getIconForMimeType(mimeType: string) {
    const iconClasses = {
      image: { icon: String.fromCharCode(0xf1c5), class: 'far', color: '#dd5044' },
      audio: { icon: String.fromCharCode(0xf1c7), class: 'far', color: '#f9a824' },
      video: { icon: String.fromCharCode(0xf1c8), class: 'far', color: '#5f6368' },
      'application/pdf': { icon: String.fromCharCode(0xf1c1), class: 'far', color: '#ea4235' },
      'application/msword': { icon: String.fromCharCode(0xf1c2), class: 'far', color: '#4185f4' },
      'application/vnd.ms-word': { icon: String.fromCharCode(0xf1c2), class: 'far', color: '#4185f4' },
      'application/vnd.oasis.opendocument.text': { icon: String.fromCharCode(0xf1c2), class: 'far', color: '#4185f4' },
      'application/vnd.openxmlformats-officedocument.wordprocessingml': { icon: String.fromCharCode(0xf1c2), class: 'far', color: '#4185f4' },
      'application/vnd.ms-excel': { icon: String.fromCharCode(0xf1c3), class: 'far', color: '#0b9d58' },
      'application/vnd.openxmlformats-officedocument.spreadsheetml': { icon: String.fromCharCode(0xf1c3), class: 'far', color: '#0b9d58' },
      'application/vnd.oasis.opendocument.spreadsheet': { icon: String.fromCharCode(0xf1c3), class: 'far', color: '#0b9d58' },
      'application/vnd.ms-powerpoint': { icon: String.fromCharCode(0xf1c4), class: 'far', color: '#f4b927' },
      'application/vnd.openxmlformats-officedocument.presentationml': { icon: String.fromCharCode(0xf1c4), class: 'far', color: '#f4b927' },
      'application/vnd.oasis.opendocument.presentation': { icon: String.fromCharCode(0xf1c4), class: 'far', color: '#f4b927' },
      'text/plain': { icon: String.fromCharCode(0xf15c), class: 'far', color: '#4185f4' },
      'text/html': { icon: String.fromCharCode(0xf1c9), class: 'far', color: '#4185f4' },
      'application/json': { icon: String.fromCharCode(0xf1c9), class: 'far', color: '#4185f4' },
      'application/gzip': { icon: String.fromCharCode(0xf1c6), class: 'far', color: '#737373' },
      'application/zip': { icon: String.fromCharCode(0xf1c6), class: 'far', color: '#737373' }
    };
    for (const key in iconClasses) {
      if (Object.prototype.hasOwnProperty.call(iconClasses, key)) {
        if (mimeType && mimeType.search(key) === 0) {
          return ld.get(iconClasses, key);
        }
      } else {
        return { icon: String.fromCharCode(0xf15b), class: 'far', color: '#5f6368' };
      }
    }
    return { icon: String.fromCharCode(0xf15b), class: 'far', color: '#5f6368' };
  }

  public static generateUUID() {
    let dt = new Date().getTime();
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
    return uuid;
  }

  public static getPropertyValues(arr: Array<any>, property: string, valueConverter: ValueConverter) {
    const values = [];
    if (arr && arr.length > 0) {
      for (const i in arr) {
        let value = ld.get(arr[i], property);
        if (valueConverter) {
          value = valueConverter(value);
        }
        values.push(value);
      }
    }
    return values;
  }

  public static booleanToString(value: any) {
    return value === true || value === 'true' || value === 'Y' || value === 1 || value === '1' || value === 'TRUE' || value === 'y' ? 'Y' : 'N';
  }

  public static getRandomInt(min: number, max: number): number {
    min = Math.ceil(min);
    max = Math.floor(max);
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  public static getRandomColor() {
    return planbookColors[CommonUtils.getRandomInt(0, planbookColors.length - 1)];
  }

  public static isArrayEqualDeepCompare(array1: Array<any>, array2: Array<any>) {
    return ld(array1).xorWith(array2, ld.isEqual).isEmpty();
  }

  public static isEmailValid(email: string) {
    return CommonUtils.hasText(email) && email.match(emailFormat);
  }

  public static hashPassword(password: string) {
    return hashSync(password, genSaltSync(5));
  }

  public static isEven(n: number) {
    return n % 2 === 0;
  }

  public static isHTMLEmpty(html: string) {
    if (CommonUtils.hasText(html)) {
      const el = document.createElement('div');
      el.innerHTML = html;
      return CommonUtils.hasNoText(el.innerText);
    }
    return true;
  }

  public static isHTMLNotEmpty(html: string) {
    return !CommonUtils.isHTMLEmpty(html);
  }

  public static getBrowserHeight() {
    return CommonUtils.getBrowserDimensions().height;
  }

  public static getBrowserWidth() {
    return CommonUtils.getBrowserDimensions().width;
  }

  public static getBrowserDimensions(): any {
    return {
      width: (window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth),
      height: (window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight)
    };
  }

  public static showLoading(): void {
    store.commit('settings/showLoading', null, { root: true });
  }

  public static hideLoading(): void {
    store.commit('settings/hideLoading', null, { root: true });
  }

  public static showProgress(): void {
    store.commit('settings/showProgress', null, { root: true });
  }

  public static hideProgress(): void {
    store.commit('settings/hideProgress', null, { root: true });
  }

  public static doSignUp() {
    const hostname = window.location.hostname;
    let url = new URL('https://app.planbook.com/signup');

    if (hostname === 'test-welcome.planbook.com' || hostname === 'test.planbook.com') {
      url = new URL('https://test-app.planbook.com/signup');
    } else if (hostname === 'staging-welcome.planbook.com' || hostname === 'staging.planbook.com') {
      url = new URL('https://staging-app.planbook.com/signup');
    } else if (hostname === 'localhost') {
      url = new URL('http://localhost:8090/signup');
    } else if (hostname.endsWith('planbookbeta.com')) {
      url = new URL('https://app.planbookbeta.com/signup');
    }

    // Add current locale as query parameter
    const currentLocale = localStorage.getItem('planbook-locale') || 'en';
    url.searchParams.set('locale', currentLocale);

    window.location.href = url.href;
  }

  public static doLogin() {
    const hostname = window.location.hostname;
    let url = new URL('https://app.planbook.com/login');

    if (hostname === 'test-welcome.planbook.com' || hostname === 'test.planbook.com') {
      url = new URL('https://test-app.planbook.com/login');
    } else if (hostname === 'staging-welcome.planbook.com' || hostname === 'staging.planbook.com') {
      url = new URL('https://staging-app.planbook.com/login');
    } else if (hostname === 'localhost') {
      url = new URL('http://localhost:8090/login');
    } else if (hostname.endsWith('planbookbeta.com')) {
      url = new URL('https://app.planbookbeta.com/login');
    }

    // Add current locale as query parameter
    const currentLocale = localStorage.getItem('planbook-locale') || 'en';
    url.searchParams.set('locale', currentLocale);

    window.location.href = url.href;
  }

  public static sanitizeQuery(query: Record<string, unknown>): Record<string, string> {
    const sanitized: Record<string, string> = {};
    for (const [key, value] of Object.entries(query)) {
      if (typeof value === 'string') {
        sanitized[key] = value;
      } else if (Array.isArray(value) && typeof value[0] === 'string') {
        sanitized[key] = value[0];
      }
    }
    return sanitized;
  }
}
